import { useMemo } from 'react';

import type { StockVenueModel, VenueModel } from '@snowit/types/venues';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import usePartners from '../usePartners';
import useSchemas from '../useSchemas';
import useTenants from '../useTenants';

export default () => {
  const firebase = useFirebase();
  const { activeTenants: tenants, hasTenants } = useTenants();
  const { writeDocument, writeSubDocument, deleteSubDocument, deleteDocument } = useSchemas();
  const { partner } = usePartners();
  const { store, setStore } = useStore();

  const venues = useMemo(() => {
    return store.venues.list
      .filter((venue) => {
        if (!tenants.length) return true;
        return hasTenants(venue.tenants);
      })
      .filter((venue) => {
        if (!partner) return true;
        return venue.partnerUID === partner;
      });
  }, [store.venues.list, tenants, partner]);

  const getVenues = async (): Promise<VenueModel[]> => {
    try {
      const res = await firebase.query<VenueModel>({
        collectionName: 'venues',
        filters: (q) => q.limit(0),
      });
      setStore('venues', { list: res });
      return res;
    } catch (error) {
      console.error('venues_get', { error });
      return [];
    } finally {
      setStore('venues', { ready: true });
    }
  };

  const getStocks = async (venueUID: string): Promise<StockVenueModel[]> => {
    try {
      const ref = await firebase.subcollection<StockVenueModel>({
        collectionName: 'venues',
        id: venueUID,
        subcollectionName: 'stocks',
      });
      return ref.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
    } catch (error) {
      console.error({ error });
      return [];
    }
  };

  const writeVenue = async (
    venueUID: string | undefined,
    data: Partial<VenueModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.VENUE,
        attributes: omitter(data),
        id: isNewEntity(venueUID) ? undefined : venueUID,
      });
      return id;
    } catch (error: any) {
      console.error('writeVenue', { error });
    }
  };

  const deleteVenue = async (venueUID: string) => {
    try {
      if (isNewEntity(venueUID)) return;
      await deleteDocument({
        modelName: SchemaNames.VENUE,
        id: venueUID,
      });
    } catch (error) {
      console.error('deleteVenue', { error });
    }
  };

  const writeStocks = async (
    venueUID: string,
    stockUID: string | undefined,
    data: Partial<StockVenueModel>,
  ): Promise<string | undefined> => {
    try {
      if (isNewEntity(venueUID)) return undefined;
      const id = await writeSubDocument({
        attributes: omitter(data),
        modelName: SchemaNames.VENUE_STOCK,
        parentId: venueUID,
        id: isNewEntity(stockUID) ? undefined : stockUID,
      });
      return id;
    } catch (error: any) {
      console.error('writeVenueStocks', { error });
    }
  };

  const deleteStock = async (stockUID: string, venueUID: string) => {
    try {
      if (isNewEntity(venueUID) || isNewEntity(stockUID)) return;
      await deleteSubDocument({
        modelName: SchemaNames.VENUE_STOCK,
        id: stockUID,
        parentId: venueUID,
      });
    } catch (error) {
      console.error('deleteStock', { error });
    }
  };

  return {
    venues,
    getVenues,
    writeVenue,
    deleteVenue,
    getStocks,
    writeStocks,
    deleteStock,
    ready: store.venues.ready,
  };
};
